/* 
  Zaktualizowano PropertiesPanel:
  1. Dodano parametr "Kolor" dla elementu "Przejście" (OPENING).
  2. Zamieniono statyczne wyświetlanie kodu HEX na interaktywne pole tekstowe (input),
     co umożliwia kopiowanie (Ctrl+C) i wklejanie (Ctrl+V) wartości kolorów.
  3. Zastosowano tę zmianę we wszystkich miejscach z wyborem koloru (Pomieszczenie, Ściana, Elementy, Powierzchnia).
*/
import React, { useState, useEffect } from 'react';
import useSceneStore from '../store/useSceneStore';
import { ELEMENT_TYPES, HATCH_PATTERNS } from '../core/constants';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';

const { FiTrash2, FiRepeat, FiCopy, FiArrowUp, FiInfo, FiLayers } = FiIcons;

const PropertiesPanel = () => {
  const { 
    walls, corners, elements, rooms, surfaces, dimensionLines,
    selectedCornerId, selectedWallId, selectedElementId, selectedRoomId, selectedSurfaceId, selectedDimensionId,
    updateWallThickness, updateWallStyle, updateWallLength, deleteElement, updateElementWidth, updateElementDepth, updateElementColor, updateElementFlip, duplicateElement,
    updateRoomName, updateRoomFillColor, updateRoomFillOpacity, updateRoomWallThickness, updateRoomWallColor, updateRoomManualArea, updateRoomHatch, applyRoomPropertiesToWalls,
    updateSurface, deleteSurface, deleteWall, deleteRoom,
    updateDimensionValue, deleteDimension,
    bringToFront
  } = useSceneStore();

  const [resizeAnchor, setResizeAnchor] = useState('left');

  useEffect(() => { if (selectedWallId) setResizeAnchor('left'); }, [selectedWallId]);

  const selectedWall = selectedWallId ? walls[selectedWallId] : null;
  const selectedElement = selectedElementId ? elements[selectedElementId] : null;
  const selectedRoom = selectedRoomId ? rooms[selectedRoomId] : null;
  const selectedSurface = selectedSurfaceId ? surfaces[selectedSurfaceId] : null;
  const selectedDimension = selectedDimensionId ? dimensionLines[selectedDimensionId] : null;

  const getWallLength = (wall) => {
    if (!wall || !corners[wall.startCornerId] || !corners[wall.endCornerId]) return 0;
    const s = corners[wall.startCornerId], e = corners[wall.endCornerId];
    return Math.sqrt(Math.pow(e.x - s.x, 2) + Math.pow(e.y - s.y, 2));
  };

  const handleWallLengthChange = (newLength) => {
    if (!selectedWall) return;
    const storeAnchor = resizeAnchor === 'left' ? 'start' : (resizeAnchor === 'right' ? 'end' : 'center');
    updateWallLength(selectedWallId, newLength, storeAnchor);
  };

  // Helper to render copy-pasteable hex input
  const ColorInput = ({ label, value, onChange }) => (
    <div className="flex items-center justify-between">
      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{label}</label>
      <div className="flex items-center gap-2">
        <input 
          type="text"
          value={value || '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          className="w-16 h-7 bg-gray-50 border border-gray-200 rounded text-[10px] font-mono text-gray-600 px-1 focus:ring-1 focus:ring-indigo-500 outline-none uppercase"
          placeholder="#FFFFFF"
        />
        <input 
          type="color" 
          value={value || '#ffffff'} 
          onChange={(e) => onChange(e.target.value)} 
          className="w-8 h-8 rounded-lg border border-gray-200 cursor-pointer p-0.5 overflow-hidden" 
        />
      </div>
    </div>
  );

  if (!selectedWallId && !selectedElementId && !selectedRoomId && !selectedSurfaceId && !selectedDimensionId) return null;

  return (
    <div className="absolute left-20 top-20 w-72 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden z-20 animate-in fade-in slide-in-from-left-2 duration-200">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 text-center">
        <h3 className="font-semibold text-gray-700 text-xs uppercase tracking-widest">
          {selectedRoom ? 'Właściwości Pomieszczenia' : selectedWall ? 'Właściwości Ściany' : selectedSurface ? 'Właściwości Powierzchni' : selectedDimension ? 'Właściwości Wymiaru' : 'Właściwości Elementu'}
        </h3>
      </div>
      
      <div className="p-4 space-y-5 max-h-[75vh] overflow-y-auto custom-scrollbar">
        
        {/* ROOM PROPERTIES */}
        {selectedRoom && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Nazwa pomieszczenia</label>
              <input 
                type="text" 
                value={selectedRoom.name || ''} 
                onChange={(e) => updateRoomName(selectedRoomId, e.target.value)} 
                className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all" 
                placeholder="np. Salon, Kuchnia..." 
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Powierzchnia pomieszczenia (m²)</label>
              <input 
                type="number" 
                step="0.01"
                value={selectedRoom.manualArea !== null && selectedRoom.manualArea !== undefined ? selectedRoom.manualArea : ''} 
                onChange={(e) => updateRoomManualArea(selectedRoomId, e.target.value === '' ? null : parseFloat(e.target.value))} 
                className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 text-sm font-mono focus:ring-2 focus:ring-indigo-500 outline-none" 
                placeholder={selectedRoom.area.toFixed(2)}
              />
            </div>

            <div className="pt-2 border-t border-gray-100 space-y-4">
              <ColorInput 
                label="Kolor wypełnienia" 
                value={selectedRoom.fillColor} 
                onChange={(color) => updateRoomFillColor(selectedRoomId, color)} 
              />

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Szraf (Wzór)</label>
                <select 
                  value={selectedRoom.hatchPattern || HATCH_PATTERNS.NONE}
                  onChange={(e) => updateRoomHatch(selectedRoomId, { hatchPattern: e.target.value })}
                  className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {Object.entries(HATCH_PATTERNS).map(([key, label]) => (
                    <option key={key} value={label}>{label}</option>
                  ))}
                </select>
              </div>

              {selectedRoom.hatchPattern && selectedRoom.hatchPattern !== HATCH_PATTERNS.NONE && (
                <div className="pt-1 animate-in fade-in duration-200">
                  <ColorInput 
                    label="Kolor szrafu" 
                    value={selectedRoom.hatchColor} 
                    onChange={(color) => updateRoomHatch(selectedRoomId, { hatchColor: color })} 
                  />
                </div>
              )}

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Przezroczystość: {selectedRoom.fillOpacity || 50}%</label>
                  <div className="flex bg-gray-100 rounded-md p-0.5">
                    <button 
                      onClick={() => updateRoomFillOpacity(selectedRoomId, 50)} 
                      className={clsx("px-2 py-0.5 text-[9px] font-bold rounded transition-all", (selectedRoom.fillOpacity || 50) < 100 ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700")}
                    >
                      Przezroczysty
                    </button>
                    <button 
                      onClick={() => updateRoomFillOpacity(selectedRoomId, 100)} 
                      className={clsx("px-2 py-0.5 text-[9px] font-bold rounded transition-all", (selectedRoom.fillOpacity || 50) === 100 ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700")}
                    >
                      Pełny
                    </button>
                  </div>
                </div>
                <input 
                  type="range" 
                  min="0" max="100" 
                  value={selectedRoom.fillOpacity || 50} 
                  onChange={(e) => updateRoomFillOpacity(selectedRoomId, parseInt(e.target.value))} 
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" 
                />
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100 space-y-3">
              <div className="text-[10px] font-black text-gray-800 uppercase tracking-widest border-l-2 border-indigo-500 pl-2">ŚCIANA</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-gray-400 uppercase">Grubość (cm)</label>
                  <input 
                    type="number" 
                    value={selectedRoom.wallThickness || 16} 
                    onChange={(e) => updateRoomWallThickness(selectedRoomId, parseInt(e.target.value))} 
                    className="w-full h-8 bg-gray-50 border border-gray-200 rounded-md px-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500 outline-none" 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-gray-400 uppercase">Kolor</label>
                  <div className="flex items-center gap-2 h-8">
                    <input 
                      type="color" 
                      value={selectedRoom.wallColor || '#000000'} 
                      onChange={(e) => updateRoomWallColor(selectedRoomId, e.target.value)} 
                      className="flex-1 h-full rounded border border-gray-200 cursor-pointer p-0.5" 
                    />
                  </div>
                </div>
              </div>
              <button 
                onClick={() => applyRoomPropertiesToWalls(selectedRoomId)}
                className="w-full py-2 bg-white border border-indigo-200 text-indigo-600 rounded-lg text-xs font-bold hover:bg-indigo-50 transition-colors uppercase tracking-tight"
              >
                Zastosuj do ścian
              </button>
            </div>

            <div className="pt-4 border-t border-gray-100 space-y-1.5 text-[10px]">
              <div className="flex justify-between text-gray-400 font-medium">
                <span>ID Pomieszczenia:</span>
                <span className="font-mono text-gray-600 uppercase">{selectedRoom.id.substring(0, 8)}</span>
              </div>
              <div className="flex justify-between text-gray-400 font-medium">
                <span>Powierzchnia (obl.):</span>
                <span className="font-mono text-gray-900 font-bold">{selectedRoom.area.toFixed(2)} m²</span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 space-y-2">
              <button 
                onClick={() => bringToFront(selectedRoomId, 'room')} 
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-bold"
              >
                <SafeIcon icon={FiArrowUp} className="text-sm" /> Przesuń na górę
              </button>
              <button 
                onClick={() => deleteRoom(selectedRoomId)} 
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 border border-red-100 rounded-lg hover:bg-red-100 transition-colors text-xs font-bold"
              >
                <SafeIcon icon={FiTrash2} className="text-sm" /> Usuń pomieszczenie
              </button>
            </div>
          </div>
        )}

        {/* WALL PROPERTIES */}
        {selectedWall && (
           <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Grubość (cm)</label>
                <input 
                  type="number" 
                  value={selectedWall.thickness || 16} 
                  onChange={(e) => updateWallThickness(selectedWallId, parseInt(e.target.value))} 
                  className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 text-sm font-mono focus:ring-2 focus:ring-indigo-500 outline-none" 
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Rodzaj linii</label>
                <select 
                  value={selectedWall.lineStyle || 'solid'}
                  onChange={(e) => updateWallStyle(selectedWallId, { lineStyle: e.target.value })}
                  className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="solid">Ciągła</option>
                  <option value="dashed">Przerywana</option>
                  <option value="dotted">Kropkowana</option>
                  <option value="dash-dot">Kreska-kropka</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Długość (cm)</label>
                <div className="flex flex-col gap-2">
                  <input 
                    type="number" 
                    value={Math.round(getWallLength(selectedWall))} 
                    onChange={(e) => handleWallLengthChange(parseInt(e.target.value))} 
                    className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 text-sm font-mono focus:ring-2 focus:ring-indigo-500 outline-none" 
                  />
                  <div className="grid grid-cols-3 gap-1 bg-gray-100 p-1 rounded-lg">
                    <button 
                      onClick={() => setResizeAnchor('left')}
                      className={clsx("py-1 text-[9px] font-bold rounded transition-all", resizeAnchor === 'left' ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700")}
                    >
                      Od lewej
                    </button>
                    <button 
                      onClick={() => setResizeAnchor('center')}
                      className={clsx("py-1 text-[9px] font-bold rounded transition-all", resizeAnchor === 'center' ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700")}
                    >
                      Od środka
                    </button>
                    <button 
                      onClick={() => setResizeAnchor('right')}
                      className={clsx("py-1 text-[9px] font-bold rounded transition-all", resizeAnchor === 'right' ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700")}
                    >
                      Od prawej
                    </button>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <ColorInput 
                  label="Kolor" 
                  value={selectedWall.color} 
                  onChange={(color) => updateWallStyle(selectedWallId, { color })} 
                />
              </div>

              <div className="pt-2 flex flex-col gap-2">
                <button 
                  onClick={() => bringToFront(selectedWallId, 'wall')} 
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-bold"
                >
                  <SafeIcon icon={FiArrowUp} /> Przesuń na górę
                </button>
                <button 
                  onClick={() => deleteWall(selectedWallId)} 
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 border border-red-100 rounded-lg hover:bg-red-100 transition-colors text-xs font-bold"
                >
                  <SafeIcon icon={FiTrash2} /> Usuń ścianę
                </button>
              </div>
           </div>
        )}

        {/* ELEMENT PROPERTIES (DOOR/WINDOW/SLIDING DOOR/OPENING) */}
        {selectedElement && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Kierunek otwierania</div>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => updateElementFlip(selectedElementId, 'horizontal')}
                  className={clsx("flex flex-col items-center gap-1 p-2 border rounded-lg transition-all", selectedElement.flipX ? "bg-indigo-50 border-indigo-200 text-indigo-600" : "hover:bg-gray-50 border-gray-200 text-gray-600")}
                >
                  <SafeIcon icon={FiRepeat} className="text-lg" />
                  <span className="text-[9px] font-bold uppercase">Lewa/Prawa</span>
                </button>
                <button 
                  onClick={() => updateElementFlip(selectedElementId, 'vertical')}
                  className={clsx("flex flex-col items-center gap-1 p-2 border rounded-lg transition-all", selectedElement.flipY ? "bg-indigo-50 border-indigo-200 text-indigo-600" : "hover:bg-gray-50 border-gray-200 text-gray-600")}
                >
                  <SafeIcon icon={FiRepeat} className="text-lg rotate-90" />
                  <span className="text-[9px] font-bold uppercase">Wewnątrz/Zewn.</span>
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Szerokość (cm): {selectedElement.width || 80}</label>
                <input 
                  type="range" 
                  min="40" max="300" 
                  value={selectedElement.width || 80} 
                  onChange={(e) => updateElementWidth(selectedElementId, parseInt(e.target.value))} 
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" 
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Głębokość (cm): {selectedElement.depth || 15}</label>
                <input 
                  type="range" 
                  min="5" max="100" 
                  value={selectedElement.depth || 15} 
                  onChange={(e) => updateElementDepth(selectedElementId, parseInt(e.target.value))} 
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" 
                />
              </div>

              {/* ✅ ROZSZERZONA SEKCJA: Kolor (teraz także dla Przejście) */}
              {(selectedElement.type === ELEMENT_TYPES.SLIDING_DOOR || selectedElement.type === ELEMENT_TYPES.OPENING) && (
                <div className="pt-2 border-t border-gray-100 animate-in fade-in duration-200">
                  <ColorInput 
                    label="Kolor elementu" 
                    value={selectedElement.fillColor} 
                    onChange={(color) => updateElementColor(selectedElementId, color)} 
                  />
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-gray-200 space-y-2">
              <button 
                onClick={() => bringToFront(selectedElementId, 'element')} 
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-bold"
              >
                <SafeIcon icon={FiArrowUp} /> Przesuń na górę
              </button>
              <button 
                onClick={() => duplicateElement(selectedElementId)} 
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-bold"
              >
                <SafeIcon icon={FiCopy} /> Duplikuj
              </button>
              <button 
                onClick={() => deleteElement(selectedElementId)} 
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-bold"
              >
                <SafeIcon icon={FiTrash2} /> Usuń
              </button>
            </div>
          </div>
        )}

        {/* SURFACE PROPERTIES */}
        {selectedSurface && (
          <div className="space-y-4">
            <ColorInput 
              label="Kolor wypełnienia" 
              value={selectedSurface.fillColor} 
              onChange={(color) => updateSurface(selectedSurfaceId, { fillColor: color })} 
            />
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Przezroczystość: {Math.round((selectedSurface.fillOpacity || 0.5) * 100)}%</label>
              <input 
                type="range" 
                min="0.1" max="1" step="0.1"
                value={selectedSurface.fillOpacity || 0.5} 
                onChange={(e) => updateSurface(selectedSurfaceId, { fillOpacity: parseFloat(e.target.value) })} 
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" 
              />
            </div>
            <div className="pt-2 flex flex-col gap-2">
              <button 
                onClick={() => bringToFront(selectedSurfaceId, 'surface')} 
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-bold"
              >
                <SafeIcon icon={FiArrowUp} /> Przesuń na górę
              </button>
              <button 
                onClick={() => deleteSurface(selectedSurfaceId)} 
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-bold"
              >
                <SafeIcon icon={FiTrash2} /> Usuń
              </button>
            </div>
          </div>
        )}

        {/* DIMENSION PROPERTIES */}
        {selectedDimension && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Wartość wymiaru (opcjonalnie)</label>
              <input 
                type="text" 
                value={selectedDimension.customValue || ''} 
                onChange={(e) => updateDimensionValue(selectedDimensionId, e.target.value === '' ? null : e.target.value)} 
                className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" 
                placeholder="Pozostaw puste dla auto"
              />
            </div>
            <button 
              onClick={() => deleteDimension(selectedDimensionId)} 
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-bold"
            >
              <SafeIcon icon={FiTrash2} /> Usuń wymiar
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default PropertiesPanel;